/** Gemini Live transport. No scoring or next-stage decisions belong here.
 * Official API contract: https://ai.google.dev/gemini-api/docs/live-api/capabilities
 * Browser authorization: https://ai.google.dev/gemini-api/docs/live-api/ephemeral-tokens
 */
import { atcSpokenFor } from '../../shared/radiotelephony.js';
const noop = () => {};
const armedLoudRms = 0.02;
const minArmedLoudMs = 300;
const maxArmedFrames = 300;
// Exact-zero frames come from browser noise suppression, not from the room: they use up the seed window but never set the floor.
const digitalSilenceRms = 0.0005;
const uid = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function bytesToBase64(bytes) {
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  }
  return btoa(binary);
}

export function base64ToBytes(text) {
  const binary = atob(text);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function pcm16ToFloat32(bytes) {
  if (bytes.byteLength % 2) throw new Error('Gemini returned an incomplete PCM16 sample.');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const samples = new Float32Array(bytes.byteLength / 2);
  for (let index = 0; index < samples.length; index += 1) samples[index] = view.getInt16(index * 2, true) / 32768;
  return samples;
}

export function pcm16Rms(bytes) {
  if (!bytes?.byteLength) return 0;
  const view = ArrayBuffer.isView(bytes) ? new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength) : new DataView(bytes);
  const count = Math.floor(view.byteLength / 2);
  let sum = 0;
  for (let index = 0; index < count; index += 1) { const sample = view.getInt16(index * 2, true) / 32768; sum += sample * sample; }
  return count ? Math.sqrt(sum / count) : 0;
}

export function createSpeechDetector({ silenceMs = 2000, onsetMs = 200, minThreshold = 0.012, maxThreshold = 0.3, noiseMultiplier = 3, seedMs = 500, provisionalThreshold = 0.05 } = {}) {
  const clampNoise = (value) => Math.min(0.1, Math.max(0.002, value));
  const clampFloor = (value) => Math.min(0.03, Math.max(0.002, value));
  const limit = (value) => Math.min(maxThreshold, Math.max(minThreshold, value * noiseMultiplier));
  let seeds, seededMs, noise, speaking, loudMs, silentMs, loudRunMs, threshold;
  const reset = (options = {}) => {
    const keep = options.keepNoise ? noise : null;
    seeds = []; seededMs = 0; noise = keep; speaking = false; loudMs = 0; silentMs = 0; loudRunMs = 0;
    threshold = keep === null ? provisionalThreshold : limit(keep);
  };
  const seed = () => {
    noise = seeds.length ? clampFloor(Math.min(...seeds)) : 0.004;
    seeds = []; seededMs = 0;
    threshold = limit(noise);
  };
  reset();
  const judge = (rms, frameMs) => {
    if (!speaking) {
      if (rms < threshold) { loudMs = 0; if (noise !== null) { noise = noise * 0.9 + clampNoise(rms) * 0.1; threshold = limit(noise); } return 'quiet'; }
      loudMs += frameMs;
      if (loudMs < onsetMs) return 'quiet';
      if (noise === null) seed();
      speaking = true; silentMs = 0; loudRunMs = 0;
      return 'onset';
    }
    if (rms < threshold * 0.8) {
      loudRunMs = 0; silentMs += frameMs;
      if (silentMs < silenceMs) return 'speech';
      speaking = false; loudMs = 0; silentMs = 0;
      return 'end';
    }
    // A loud bump shorter than onsetMs does not restart the silence count, so a steady hum cannot hold the utterance open.
    loudRunMs += frameMs;
    if (loudRunMs >= onsetMs) silentMs = 0;
    return 'speech';
  };
  return {
    push(rms, frameMs) {
      // Exact-zero frames still use up the seed window, but are never collected as floor evidence (seed() falls back to 0.004).
      // The frame that closes the window is judged before it seeds, so loud speech cannot raise the threshold above its own onset.
      const collecting = noise === null && !speaking;
      if (collecting) { if (rms >= digitalSilenceRms) seeds.push(rms); seededMs += frameMs; }
      const verdict = judge(rms, frameMs);
      if (collecting && noise === null && seededMs >= seedMs) seed();
      return verdict;
    },
    reset,
    get speaking() { return speaking; },
    get threshold() { return threshold; },
    get noise() { return noise; },
  };
}

function withTimeout(promise, milliseconds, message) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(message)), milliseconds); }),
  ]).finally(() => clearTimeout(timer));
}

async function createGoogleClient(token, authorization) {
  if (authorization?.provider === 'firebase-vertex-relay') {
    const { createVertexRelayClient } = await import('./vertexRelay.js');
    return createVertexRelayClient(authorization);
  }
  const { GoogleGenAI } = await import('@google/genai');
  // Ephemeral tokens require v1beta. The only credential accepted is returned by
  // the authenticated backend; there is deliberately no VITE API-key path.
  return new GoogleGenAI({ apiKey: token, httpOptions: { apiVersion: 'v1beta' } });
}

export class LiveVoiceClient {
  constructor({ requestToken, onState = noop, onTranscript = noop, onTurnComplete = noop,
    onError = noop, onToolCall, dependencies = {}, timeoutMs = 20000, turnTimeoutMs = 30000,
    autoReconnect = false, maxAutoReconnects = 2, handsFree = false, silenceMs = 2000, maxUtteranceMs = 20000,
    rearmDelayMs = 250, preRollFrames = 8, commitMs = 400, responseFallbackMs = 6000 } = {}) {
    if (typeof requestToken !== 'function') throw new Error('A backend token callback is required.');
    this.requestToken = requestToken;
    this.onState = onState;
    this.onTranscript = onTranscript;
    this.onTurnComplete = onTurnComplete;
    this.onError = onError;
    this.onToolCall = onToolCall;
    this.dependencies = dependencies;
    this.timeoutMs = timeoutMs;
    this.turnTimeoutMs = turnTimeoutMs;
    this.autoReconnect = autoReconnect;
    this.maxAutoReconnects = maxAutoReconnects;
    this.handsFree = handsFree;
    this.maxUtteranceMs = maxUtteranceMs;
    this.rearmDelayMs = rearmDelayMs;
    this.preRollFrames = preRollFrames;
    this.commitMs = commitMs;
    this.responseFallbackMs = responseFallbackMs;
    this._detector = createSpeechDetector({ silenceMs });
    this._autoListen = false;
    this._armed = false;
    this._armTimer = null;
    this._preRoll = [];
    this._armedFrames = [];
    this._armedLoudMs = 0;
    this._pending = null;
    this._streamStartedAt = 0;
    this._pendingTurnCallbacks = 0;
    this._autoReconnects = 0;
    this.state = 'idle';
    this._generation = 0;
    this._sources = new Set();
    this._transcriptQueue = Promise.resolve();
    this._turnWrites = [];
    this._turnCallbacks = Promise.resolve();
    this._buffers = {};
    this._eventPrefix = uid();
    this._eventSequence = 0;
    this._talking = false;
    this._capturing = false;
    this._resumptionHandle = null;
    this._openingDone = false;
    this._awaitingOpening = false;
    this._response = { pilot: '', atc: '' };
    this._responseEventIds = { pilot: [], atc: [] };
    this._awaitingReply = false;
    this._responseFallbackTimer = null;
    this._lateReply = false;
  }

  _setState(state) {
    if (this.state === state) return;
    this.state = state;
    try { this.onState(state); } catch (error) { this._report(error); }
  }

  _report(error) {
    const detail = error instanceof Error ? error : new Error(error?.message || 'Voice connection failed.');
    try { Promise.resolve(this.onError(detail)).catch(noop); } catch { /* A reporting callback must not crash cleanup. */ }
    return detail;
  }

  _unlockAudio() {
    // Construct and resume synchronously in the touch/click handler, before any
    // Firebase, microphone, or network awaits. Required by iPad Safari.
    if (!this._audioContext || this._audioContext.state === 'closed') {
      const factory = this.dependencies.audioContextFactory || (() => {
        const Constructor = globalThis.AudioContext || globalThis.webkitAudioContext;
        if (!Constructor) throw new Error('This browser does not support Web Audio.');
        return new Constructor();
      });
      this._audioContext = factory();
      this._playbackTime = this._audioContext.currentTime;
    }
    return this._audioContext.resume();
  }

  connect(turn) {
    if (this._endingPromise) return Promise.reject(new Error('Voice is ending. Please wait before reconnecting.'));
    this._autoReconnects = 0;
    this._autoListen = true;
    this._openingDone = false;
    let unlocked;
    try { unlocked = this._unlockAudio(); } catch (error) { this._setState('error'); return Promise.reject(this._report(error)); }
    return this._connect(turn, { unlocked, resume: false });
  }

  reconnect() {
    if (this._endingPromise) return Promise.reject(new Error('Voice is ending. Please wait before reconnecting.'));
    if (!this._turn) return Promise.reject(new Error('Choose a stage before reconnecting.'));
    this._autoReconnects = 0;
    let unlocked;
    try { unlocked = this._unlockAudio(); } catch (error) { return Promise.reject(this._report(error)); }
    // Resume only after the fixed tower line completed: a resumed session never replays it.
    return this._connect(this._turn, { unlocked, resume: this._openingDone });
  }

  setTurn(turn) {
    // The caller validates the rubric before calling this. A fresh constrained
    // token isolates each stage instead of asking the model to invent a next one.
    return this.connect(turn);
  }

  setHandsFree(enabled) {
    this.handsFree = Boolean(enabled);
    if (this.handsFree) this._maybeAutoListen(this._generation);
    else this._cancelAutoListen();
  }

  setAutoListen(enabled) {
    this._autoListen = Boolean(enabled);
    if (this._autoListen) this._maybeAutoListen(this._generation);
    else this._cancelAutoListen();
  }

  disarm() {
    this._cancelAutoListen();
  }

  unlock() {
    try { return this._unlockAudio(); } catch (error) {
      return Promise.reject(error instanceof Error ? error : new Error(error?.message || 'Voice audio could not be unlocked.'));
    }
  }

  disconnect() {
    // The AudioContext is kept: iPad Safari only unlocks it inside a user gesture.
    if (this._endingPromise) return this._endingPromise;
    this._resumptionHandle = null;
    return this._disconnect('idle');
  }

  async _connect(turn, { unlocked, resume }) {
    if (!turn?.stageId || !turn?.turnId) throw new Error('stageId and turnId are required.');
    this._awaitingOpening = false;
    const generation = ++this._generation;
    this._pendingTurnCallbacks = 0;
    this._detector.reset();
    const oldSession = this._session;
    this._session = null;
    this._abortController?.abort();
    this._clearTurnTimer();
    this._clearResponseFallback();
    this._disposeMicrophone();
    this._stopPlayback();
    try { oldSession?.close(); } catch { /* already closed */ }
    this._flushTranscripts('partial-transcript', 'stage-change');
    this._turn = { ...turn };
    this._response = { pilot: '', atc: '' };
    this._responseEventIds = { pilot: [], atc: [] };
    this._turnWrites = [];
    this._interrupted = false;
    this._serverTurnComplete = false;
    this._lateReply = false;
    if (!resume) this._resumptionHandle = null;
    this._setState('connecting');
    this._abortController = new AbortController();
    try {
      await withTimeout(this._establish(generation, unlocked, resume), this.timeoutMs,
        'Voice connection timed out. Please reconnect.');
      return this;
    } catch (error) {
      if (generation === this._generation) await this._disconnect('error', error);
      throw error;
    }
  }

  _assertCurrent(generation) {
    if (generation !== this._generation) throw new Error('Voice connection was cancelled.');
  }

  async _establish(generation, unlocked, resume) {
    await unlocked;
    this._assertCurrent(generation);
    await this._prepareMicrophone(generation);
    this._assertCurrent(generation);
    const resumptionHandle = resume ? this._resumptionHandle : null;
    const authorization = await this.requestToken({ stageId: this._turn.stageId, turnId: this._turn.turnId,
      ...(resumptionHandle ? { resumptionHandle } : {}) });
    this._assertCurrent(generation);
    const supported = authorization?.provider === 'firebase-vertex-relay'
      ? ['gemini-3.8-live','gemini-live-2.5-flash-native-audio'].includes(authorization.model) && Boolean(authorization.endpoint)
      : authorization?.model === 'gemini-3.8-live';
    if (!authorization?.token || !supported || !authorization.config) {
      throw new Error('Backend did not return supported Gemini Live authorization and constrained config.');
    }
    const config = authorization.config;
    if (config.responseModalities?.length !== 1 || config.responseModalities[0] !== 'AUDIO'
      || !config.inputAudioTranscription || !config.outputAudioTranscription
      || config.realtimeInputConfig?.automaticActivityDetection?.disabled !== true) {
      throw new Error('Backend voice config must enable AUDIO, both transcripts, and manual activity detection.');
    }
    const client = await (this.dependencies.createClient || createGoogleClient)(authorization.token, authorization);
    this._assertCurrent(generation);
    const session = await client.live.connect({ model: authorization.model,
      config: { ...config, abortSignal: this._abortController.signal },
      callbacks: {
        onopen: noop, // SDK connect resolves after setup. Do not enable PTT early.
        onmessage: (message) => {
          if (generation !== this._generation) return;
          try { this._handleMessage(message, generation); } catch (error) { void this._disconnect('error', error); }
        },
        onerror: (error) => { if (generation === this._generation) void this._connectionLost('error', error, generation); },
        onclose: (event) => {
          if (generation === this._generation) void this._connectionLost('disconnected',
            new Error(event?.reason || 'Voice disconnected. Reconnect to retry this stage.'), generation);
        },
      },
    });
    if (generation !== this._generation) { session.close(); this._assertCurrent(generation); }
    this._session = session;
    this._setState('connected');
    // Only backend-approved resumption restores a previous context. Otherwise
    // request exactly the fixed tower line for an ATC-initiated stage.
    const resumed = Boolean(resumptionHandle && config.sessionResumption?.handle === resumptionHandle);
    if (!resumed && this._turn.initiation === 'atc') {
      if (!this._turn.atcText) throw new Error('An ATC-initiated stage requires a fixed tower line.');
      this._setState('processing');
      const text = `Speak this stage's fixed tower line exactly once: ${atcSpokenFor(this._turn)}\nThen wait for the pilot. Do not grade, advance, or add instructions.`;
      this._awaitingOpening = true;
      this._openingDone = false;
      if (authorization.provider === 'firebase-vertex-relay') {
        // The relay ignores client wording and speaks the server-authorized line.
        session.sendClientContent({ turns: [{ role: 'user', parts: [{ text }] }], turnComplete: true });
      } else session.sendRealtimeInput({ text });
      this._startTurnTimer(generation);
    } else {
      if (!resumed) this._openingDone = true;
      this._maybeAutoListen(generation);
    }
  }

  async _prepareMicrophone(generation) {
    const getUserMedia = this.dependencies.getUserMedia
      || ((constraints) => globalThis.navigator?.mediaDevices?.getUserMedia(constraints));
    const pending = getUserMedia({ audio: { channelCount: 1, echoCancellation: true,
      noiseSuppression: true, autoGainControl: true } });
    if (!pending) throw new Error('Microphone requires HTTPS and browser permission.');
    const stream = await pending;
    if (generation !== this._generation) { stream.getTracks().forEach((track) => track.stop()); this._assertCurrent(generation); }
    this._stream = stream;
    const context = this._audioContext;
    if (!context.audioWorklet) throw new Error('AudioWorklet is unavailable. Use a current Safari or Chrome browser over HTTPS.');
    await context.audioWorklet.addModule(this.dependencies.workletUrl || `${import.meta.env?.BASE_URL || '/'}pcm-worklet.js`);
    this._assertCurrent(generation);
    this._micSource = context.createMediaStreamSource(stream);
    const makeNode = this.dependencies.createWorkletNode || ((ctx) => new AudioWorkletNode(ctx, 'pilot-pcm'));
    this._worklet = makeNode(context);
    this._muteGain = context.createGain();
    this._muteGain.gain.value = 0;
    this._worklet.port.onmessage = ({ data }) => {
      if (generation !== this._generation) return;
      if (data.type === 'pcm' && this._armed && this._session) {
        const rms = pcm16Rms(data.buffer), frameMs = data.buffer.byteLength / 2 / 16;
        this._preRoll.push(data.buffer);
        if (this._preRoll.length > this.preRollFrames) this._preRoll.shift();
        // Keep quiet speech that never reaches onset, so a manual Stop & send can still deliver it.
        this._armedFrames.push(data.buffer);
        if (this._armedFrames.length > maxArmedFrames) this._armedFrames.shift();
        // Counts every frame since arming, including frames the 30 s buffer has already dropped.
        if (rms >= (this._detector.noise === null ? this._detector.threshold : Math.max(armedLoudRms, 1.5 * this._detector.noise))) this._armedLoudMs += frameMs;
        this._hearArmed(generation, data.buffer, rms, frameMs, this._detector.push(rms, frameMs));
      } else if (data.type === 'pcm' && this._capturing && this._session) {
        try { this._session.sendRealtimeInput({ audio: {
          data: bytesToBase64(new Uint8Array(data.buffer)), mimeType: 'audio/pcm;rate=16000',
        } }); } catch (error) { void this._disconnect('error', error); return; }
        if (this.handsFree) {
          const verdict = this._detector.push(pcm16Rms(data.buffer), data.buffer.byteLength / 2 / 16);
          if (verdict === 'end' || Date.now() - this._streamStartedAt > this.maxUtteranceMs) void this.stopTalking().catch(noop);
        }
      } else if (data.type === 'flushed' && this._flushWaiter?.requestId === data.requestId) {
        this._flushWaiter.resolve();
      }
    };
    this._micSource.connect(this._worklet);
    this._worklet.connect(this._muteGain);
    this._muteGain.connect(context.destination);
  }

  async startTalking() {
    const unlocked = this._unlockAudio();
    if (this._stopping) await this._stopping;
    if (!this._session || !['connected', 'speaking', 'listening', 'armed'].includes(this.state)) {
      throw new Error('Connect voice before using the microphone.');
    }
    if (this._armed) {
      const generation = this._generation;
      await unlocked;
      if (this._armed && generation === this._generation) this._requestCommit(generation);
      return;
    }
    if (this._talking) return;
    const generation = this._generation;
    this._talking = true;
    await unlocked;
    if (!this._talking || generation !== this._generation) return;
    this._clearTurnTimer();
    this._clearResponseFallback();
    this._stopPlayback();
    this._interrupted = false;
    this._serverTurnComplete = false;
    this._capturing = true;
    this._detector.reset();
    this._streamStartedAt = Date.now();
    try {
      this._session.sendRealtimeInput({ activityStart: {} });
      this._worklet.port.postMessage({ type: 'start' });
      this._setState('listening');
      // A real Pilot action permits another bounded recovery window.
      this._autoReconnects = 0;
    } catch (error) { await this._disconnect('error', error); throw error; }
  }

  stopTalking() {
    if (this._armed) {
      // Only a student who spoke (300 ms of frames at 1.5x the room floor, or the 0.05 provisional level before it is known) is sent; room hum or a single bump is discarded.
      if (this._armedLoudMs < minArmedLoudMs || !this._session) { this._disarm(); return Promise.resolve(); }
      if (this._pendingTurnCallbacks > 0) { this._pending = { frames: [...this._armedFrames], voicedMs: 0, quietMs: 0, ended: false, manual: false, stopRequested: true }; return Promise.resolve(); }
      this._preRoll = [...this._armedFrames];
      this._beginStreaming(this._generation);
    }
    if (this._stopping) return this._stopping;
    if (!this._talking || !this._session) return Promise.resolve();
    this._talking = false;
    // A quick pointer release can occur while Safari resume() is pending.
    // Cancel that start without emitting an unmatched activityEnd.
    if (!this._capturing) return Promise.resolve();
    const generation = this._generation;
    this._stopping = this._finishTalking(generation).finally(() => { this._stopping = null; });
    return this._stopping;
  }

  async _finishTalking(generation) {
    const requestId = uid();
    const flushed = new Promise((resolve) => { this._flushWaiter = { requestId, resolve }; });
    this._worklet.port.postMessage({ type: 'stop', requestId });
    try {
      // Worklet sends its final PCM frame before the acknowledgement. The
      // activityEnd must follow that frame, otherwise the last syllable is lost.
      await withTimeout(flushed, 1000, 'Microphone did not finish its audio frame. Please reconnect.');
      if (generation !== this._generation || !this._session) return;
      this._capturing = false;
      this._session.sendRealtimeInput({ activityEnd: {} });
      this._setState('processing');
      this._startTurnTimer(generation);
      this._armResponseFallback();
    } catch (error) { if (generation === this._generation) await this._disconnect('error', error); throw error; }
    finally { if (this._flushWaiter?.requestId === requestId) this._flushWaiter = null; }
  }

  _maybeAutoListen(generation) {
    if (generation !== this._generation) return;
    this._clearArmTimer();
    if (!this._canAutoListen(generation)) return;
    this._armTimer = setTimeout(() => {
      this._armTimer = null;
      if (!this._canAutoListen(generation)) return;
      try { this._arm(generation); } catch (error) { void this._disconnect('error', error); }
    }, this.rearmDelayMs);
  }

  _canAutoListen(generation) {
    return this.handsFree && this._autoListen && Boolean(this._session) && generation === this._generation
      && this.state === 'connected' && !this._talking && !this._sources.size
      && !this._stopping && !this._endingPromise;
  }

  _arm(generation) {
    if (generation !== this._generation || !this._session || !this._worklet) return;
    this._talking = true;
    this._armed = true;
    this._capturing = false;
    this._pending = null;
    this._preRoll = [];
    this._armedFrames = [];
    this._armedLoudMs = 0;
    this._detector.reset({ keepNoise: true });
    this._worklet.port.postMessage({ type: 'start' });
    this._setState('armed');
  }

  _hearArmed(generation, buffer, rms, frameMs, verdict) {
    if (!this._pending) {
      if (verdict !== 'onset') return;
      this._pending = { frames: [...this._preRoll], voicedMs: 0, quietMs: 0, ended: false, manual: false, stopRequested: false };
    } else if (this._pending.stopRequested) {
      return;
    } else {
      this._pending.frames.push(buffer);
      if (this._pending.frames.length > maxArmedFrames) this._pending.frames.shift();
    }
    const pending = this._pending;
    // Voiced means the detector does not count the frame toward silence, so speech that starts at a clamped floor still counts.
    if (rms >= this._detector.threshold * 0.8) { pending.voicedMs += frameMs; pending.quietMs = 0; } else pending.quietMs += frameMs;
    if (verdict === 'end') pending.ended = true;
    if (!pending.manual && pending.voicedMs < this.commitMs && pending.quietMs >= 1000) return this._discardPending();
    this._tryCommitPending(generation);
  }

  _requestCommit(generation) {
    if (this._pending) this._pending.manual = true;
    else {
      this._pending = { frames: [...this._preRoll], voicedMs: 0, quietMs: 0, ended: false, manual: true, stopRequested: false };
      this._detector.reset({ keepNoise: true });
    }
    this._tryCommitPending(generation);
  }

  _tryCommitPending(generation) {
    const pending = this._pending;
    if (!pending || generation !== this._generation || this._pendingTurnCallbacks > 0) return;
    if (!pending.manual && !pending.stopRequested && !pending.ended && pending.voicedMs < this.commitMs) return;
    if (!this._autoListen) return this._disarm();
    this._preRoll = pending.frames;
    this._beginStreaming(generation);
    if (pending.ended || pending.stopRequested) void this.stopTalking().catch(noop);
  }

  _discardPending() {
    this._pending = null;
    this._preRoll = [];
    this._armedFrames = [];
    this._armedLoudMs = 0;
    // A full reset: a burst-seeded floor kept here would read the next readback as room.
    this._detector.reset();
  }

  _beginStreaming(generation) {
    if (generation !== this._generation || !this._session) return;
    this._armed = false;
    this._pending = null;
    this._capturing = true;
    this._armedFrames = [];
    this._armedLoudMs = 0;
    this._streamStartedAt = Date.now();
    this._clearTurnTimer();
    this._clearResponseFallback();
    this._stopPlayback();
    this._interrupted = false;
    this._serverTurnComplete = false;
    try {
      this._session.sendRealtimeInput({ activityStart: {} });
      for (const buffer of this._preRoll) {
        this._session.sendRealtimeInput({ audio: { data: bytesToBase64(new Uint8Array(buffer)), mimeType: 'audio/pcm;rate=16000' } });
      }
      this._preRoll = [];
      this._setState('listening');
      // A real Pilot action permits another bounded recovery window.
      this._autoReconnects = 0;
    } catch (error) { void this._disconnect('error', error); }
  }

  _disarm() {
    this._armed = false;
    this._pending = null;
    this._talking = false;
    this._preRoll = [];
    this._armedFrames = [];
    this._armedLoudMs = 0;
    this._clearArmTimer();
    this._worklet?.port.postMessage({ type: 'stop', requestId: uid() });
    if (this._session) this._setState('connected');
  }

  _cancelAutoListen() {
    if (this._armed) this._disarm();
    else this._clearArmTimer();
  }

  _clearArmTimer() { clearTimeout(this._armTimer); this._armTimer = null; }

  _appendTranscript(role, transcription) {
    if (!transcription) return;
    let buffer = this._buffers[role];
    if (!buffer && transcription.text) {
      buffer = { role, text: '', eventId: `${this._eventPrefix}-${++this._eventSequence}`,
        stageId: this._turn.stageId, turnId: this._turn.turnId, source: 'gemini-live' };
      this._buffers[role] = buffer;
      this._responseEventIds[role].push(buffer.eventId);
    }
    if (!buffer) return;
    buffer.text += transcription.text || '';
    this._response[role] += transcription.text || '';
    if (role === 'pilot' && transcription.text && this._awaitingReply) this._armResponseFallback();
    if (transcription.finished) this._flushTranscript(role, 'transcript', 'transcription-finished');
    else this._queueTranscript({ ...buffer, partial: true, kind: 'partial-transcript' });
  }

  _queueTranscript(event) {
    const task = this._transcriptQueue.catch(noop).then(() => this.onTranscript(event));
    this._transcriptQueue = task;
    // Each write records its own outcome, so a failed save can block only the turn it belongs to.
    this._turnWrites.push(task.then(() => null, (error) => ({ error })));
    task.catch((error) => this._report(error));
  }

  _flushTranscript(role, kind = 'transcript', flushReason = 'turn-complete') {
    const buffer = this._buffers[role];
    if (!buffer) return;
    delete this._buffers[role];
    if (buffer.text.trim()) this._queueTranscript({ ...buffer, partial: false, kind, flushReason });
  }

  _flushTranscripts(kind = 'transcript', flushReason = 'turn-complete') {
    this._flushTranscript('pilot', kind, flushReason);
    this._flushTranscript('atc', kind, flushReason);
  }

  _handleMessage(message, generation) {
    const update = message.sessionResumptionUpdate;
    if (update?.resumable && update.newHandle) this._resumptionHandle = update.newHandle;
    if (update?.resumable === false) this._resumptionHandle = null;
    if (message.goAway) {
      // GoAway is advance notice, not an actual socket close. Keep playing and
      // accepting Pilot calls until onclose; the same message may finish ATC's turn.
      this._goAwayTimeLeft = message.goAway.timeLeft;
    }
    const content = message.serverContent;
    if (content) {
      if (content.interrupted) {
        this._interrupted = true;
        this._clearResponseFallback();
        this._stopPlayback();
        this._flushTranscripts('partial-transcript', 'interruption');
        this._setState(this._armed ? 'armed' : this._talking ? 'listening' : 'connected');
      }
      const modelParts = content.modelTurn?.parts || [];
      if (content.outputTranscription?.text || content.outputTranscription?.finished || modelParts.some((part) => part.text || part.inlineData?.data)) this._clearResponseFallback();
      this._appendTranscript('pilot', content.inputTranscription);
      this._appendTranscript('atc', content.outputTranscription);
      // A message can contain several audio parts. Never inspect only parts[0].
      for (const part of content.modelTurn?.parts || []) {
        if (part.inlineData?.data && /^audio\/pcm(?:;|$)/i.test(part.inlineData.mimeType || '')) {
          this._playAudio(part.inlineData.data);
        }
      }
      if (content.turnComplete) {
        if (this._awaitingOpening) { this._awaitingOpening = false; this._openingDone = true; }
        // The late reply to a fallback-completed response must not complete Pilot words of the next utterance already being captured.
        const lateReply = this._lateReply && this._talking;
        this._lateReply = false;
        if (lateReply) this._flushTranscript('atc', 'transcript', 'turn-complete');
        else this._completeTurn(generation);
      }
    }
    if (message.toolCall?.functionCalls?.length) void this._handleTools(message.toolCall.functionCalls, generation);
  }

  _completeTurn(generation, { synthetic = false } = {}) {
    this._clearTurnTimer();
    this._clearResponseFallback();
    this._serverTurnComplete = true;
    this._flushTranscripts('transcript', synthetic ? 'response-fallback' : 'turn-complete');
    const completed = { stageId: this._turn.stageId, turnId: this._turn.turnId,
      pilotTranscript: this._response.pilot.trim(), atcTranscript: this._response.atc.trim(),
      pilotEventId: this._responseEventIds.pilot.at(-1) || null,
      pilotEventIds: [...this._responseEventIds.pilot], atcEventIds: [...this._responseEventIds.atc],
      interrupted: this._interrupted, source: 'gemini-live' };
    if (synthetic) { completed.synthetic = true; this._lateReply = true; }
    this._response = { pilot: '', atc: '' };
    this._responseEventIds = { pilot: [], atc: [] };
    this._interrupted = false;
    const transcriptWrites = Promise.all(this._turnWrites);
    this._turnWrites = [];
    this._pendingTurnCallbacks += 1;
    this._turnCallbacks = this._turnCallbacks.catch(noop).then(async () => {
      // A rejected save callback must never lead to a scoring callback. Only a failed write of this turn blocks it.
      const failed = (await transcriptWrites).find(Boolean);
      if (failed) throw failed.error;
      if (generation === this._generation) await this.onTurnComplete(completed);
    });
    this._turnCallbacks.catch((error) => this._report(error)).finally(() => {
      if (generation === this._generation) this._pendingTurnCallbacks -= 1;
      this._maybeAutoListen(generation);
      this._tryCommitPending(generation);
    });
    if (!this._sources.size && !this._talking) this._setState('connected');
    this._maybeAutoListen(generation);
  }

  async _handleTools(calls, generation) {
    const responses = [];
    for (const call of calls) {
      let result = { error: 'Unsupported tool. Scoring and stage advancement are controlled by the application rubric.' };
      try {
        if (this.onToolCall && !/grade|advance|evaluat|score/i.test(call.name || '')) {
          result = await this.onToolCall(call) ?? result;
        }
      }
      catch (error) { result = { error: error?.message || 'Tool unavailable.' }; this._report(error); }
      responses.push({ id: call.id, name: call.name, response: result });
    }
    if (generation === this._generation && this._session) {
      try { this._session.sendToolResponse({ functionResponses: responses }); }
      catch (error) { await this._disconnect('error', error); }
    }
  }

  _playAudio(base64) {
    if (this._armed) this._disarm();
    if (!this._audioContext || this._talking) return;
    const samples = pcm16ToFloat32(base64ToBytes(base64));
    if (!samples.length) return;
    const context = this._audioContext;
    const buffer = context.createBuffer(1, samples.length, 24000);
    buffer.copyToChannel(samples, 0);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    this._sources.add(source);
    const generation = this._generation;
    source.onended = () => {
      this._sources.delete(source);
      source.disconnect();
      if (this._session && !this._sources.size && this._serverTurnComplete && !this._talking) {
        this._setState('connected');
        this._maybeAutoListen(generation);
      }
    };
    const start = Math.max(context.currentTime + 0.015, this._playbackTime || 0);
    source.start(start);
    this._playbackTime = start + samples.length / 24000;
    this._setState('speaking');
  }

  _stopPlayback() {
    for (const source of this._sources) {
      source.onended = null;
      try { source.stop(); source.disconnect(); } catch { /* already stopped */ }
    }
    this._sources.clear();
    this._playbackTime = this._audioContext?.currentTime || 0;
  }

  _startTurnTimer(generation) {
    this._clearTurnTimer();
    this._turnTimer = setTimeout(() => {
      if (generation === this._generation) void this._disconnect('error',
        new Error('No completed voice response arrived. Your partial transcript was retained; reconnect to retry.'), 'timeout');
    }, this.turnTimeoutMs);
  }

  _clearTurnTimer() { clearTimeout(this._turnTimer); this._turnTimer = null; }

  _armResponseFallback() {
    const generation = this._generation;
    clearTimeout(this._responseFallbackTimer);
    this._awaitingReply = true;
    this._responseFallbackTimer = setTimeout(() => { this._responseFallbackTimer = null; this._completeUnansweredTurn(generation); }, this.responseFallbackMs);
  }

  _clearResponseFallback() { clearTimeout(this._responseFallbackTimer); this._responseFallbackTimer = null; this._awaitingReply = false; }

  _completeUnansweredTurn(generation) {
    if (generation !== this._generation || !this._awaitingReply || !this._response.pilot.trim()) return;
    this._completeTurn(generation, { synthetic: true });
  }

  _disposeMicrophone() {
    this._talking = false;
    this._capturing = false;
    this._armed = false;
    this._pending = null;
    this._preRoll = [];
    this._armedFrames = [];
    this._armedLoudMs = 0;
    this._clearArmTimer();
    this._flushWaiter?.resolve();
    this._flushWaiter = null;
    for (const node of [this._micSource, this._worklet, this._muteGain]) {
      try { node?.disconnect(); } catch { /* disconnected */ }
    }
    if (this._worklet) this._worklet.port.onmessage = null;
    this._stream?.getTracks().forEach((track) => track.stop());
    this._stream = this._micSource = this._worklet = this._muteGain = null;
  }

  async _disconnect(state, error, flushReason = state) {
    ++this._generation;
    this._pendingTurnCallbacks = 0;
    const session = this._session;
    this._session = null;
    this._abortController?.abort();
    this._clearTurnTimer();
    this._clearResponseFallback();
    this._disposeMicrophone();
    this._stopPlayback();
    try { session?.close(); } catch { /* already closed */ }
    this._flushTranscripts('partial-transcript', flushReason);
    this._setState(state);
    if (error) this._report(error);
    // Wait only for transcript persistence. onTurnComplete is separate so its
    // caller may safely await end() without a callback/cleanup deadlock.
    await this._transcriptQueue.catch(noop);
  }

  async _connectionLost(state, error, generation) {
    if (generation !== this._generation) return;
    // Recover only an established simulation connection. Never retry an initial
    // authorization failure or silently send interrupted Pilot audio again.
    const recover = this.autoReconnect && this._session && !this._endingPromise
      && this._autoReconnects < this.maxAutoReconnects;
    if (!recover) return this._disconnect(state, error);
    this._autoReconnects += 1;
    const nextGeneration = this._generation + 1;
    await this._disconnect('reconnecting', undefined, 'connection-lost');
    if (this._generation !== nextGeneration || this._endingPromise) return;
    try {
      await this._connect(this._turn, { unlocked: this._unlockAudio(), resume: this._openingDone });
    } catch { /* _connect already reports a failed recovery and leaves manual retry available. */ }
  }

  end() {
    if (this._endingPromise) return this._endingPromise;
    this._endingPromise = this._end().finally(() => { this._endingPromise = null; });
    return this._endingPromise;
  }

  async _end() {
    // Teardown never sends a buffered armed turn: that would start a paid Live turn whose reply is discarded.
    if (this._armed) this._disarm();
    else if (this._talking || this._stopping) await this.stopTalking().catch(noop);
    const context = this._audioContext;
    const disconnected = this._disconnect('ended');
    this._audioContext = null;
    this._resumptionHandle = null;
    await disconnected;
    if (context?.state !== 'closed') await context?.close();
  }

  async waitForCallbacks() {
    await this._transcriptQueue.catch(noop);
    await this._turnCallbacks.catch(noop);
  }
}
